import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

// ================================================
// 🎨 UTILITARIOS WHITE LABEL - EXPORTADOS CORRECTAMENTE
// ================================================

// ✅ AGORA COM EXPORT!
export const hexToRgb = (hex) => {
  try {
    if (!hex || typeof hex !== 'string') {
      return { r: 37, g: 99, b: 235 };
    }
    
    let cleanHex = hex.replace('#', '');
    if (cleanHex.length === 3) {
      cleanHex = cleanHex.split('').map(char => char + char).join('');
    }
    
    if (cleanHex.length === 6) {
      const r = parseInt(cleanHex.substring(0, 2), 16);
      const g = parseInt(cleanHex.substring(2, 4), 16);
      const b = parseInt(cleanHex.substring(4, 6), 16);
      return { r, g, b };
    }
    
    return { r: 37, g: 99, b: 235 };
  } catch (error) {
    console.error('Error converting HEX to RGB:', error);
    return { r: 37, g: 99, b: 235 };
  }
};

// ✅ AGORA COM EXPORT!
export const getWhiteLabelConfig = (company) => {
  const defaultConfig = {
    nome: '',
    slogan: '',
    mostrarMarcaDagua: false,
    cidade: '',
    uf: '',
    website: '',
    telefone: '',
    email: '',
    primaryColor: { r: 37, g: 99, b: 235 }
  };

  if (!company) return defaultConfig;

  try {
    let primaryColor = defaultConfig.primaryColor;
    if (company.cor) {
      primaryColor = hexToRgb(company.cor);
    }

    return {
      nome: company.nome || company.name || defaultConfig.nome,
      slogan: company.slogan || defaultConfig.slogan,
      mostrarMarcaDagua: company.mostrarMarcaDagua === true,
      cidade: company.cidade || '',
      uf: company.uf || '',
      website: company.website || '',
      telefone: company.telefone || company.phone || '',
      email: company.email || '',
      primaryColor
    };
  } catch (error) {
    console.error('Error extracting white label config:', error);
    return defaultConfig;
  }
};

/**
 * 📄 EXPORTAÇÃO DE RELATÓRIO FINANCEIRO EM PDF
 * 
 * Gera um PDF profissional com:
 * - Header corporativo com logo e cores da empresa (white label)
 * - KPIs executivos (receitas, despesas, saldo)
 * - Tabelas de receitas/despesas por categoria
 * - Últimos movimentos financeiros
 * - Rodapé personalizado
 * - Configurações personalizadas do usuário
 * 
 * @param {Object} params - Parâmetros da função
 * @param {string} params.mes - Mês de referência (YYYY-MM)
 * @param {Array} params.registros - Lista de registros financeiros
 * @param {number} params.totalReceitas - Total de receitas do período
 * @param {number} params.totalDespesas - Total de despesas do período
 * @param {number} params.saldo - Saldo (receitas - despesas)
 * @param {Object} params.vendas - Estatísticas de vendas (opcional)
 * @param {Object} params.alugueis - Estatísticas de aluguéis (opcional)
 * @param {string} params.empresa - Nome da empresa (white label)
 * @param {string} params.usuario - Nome do usuário/corretor
 * @param {string} params.logoBase64 - Logo da empresa em base64 (opcional)
 * @param {string} params.corPrimaria - Cor primária da empresa (hex)
 * @param {string} params.cnpj - CNPJ da empresa (opcional)
 * @param {string} params.telefone - Telefone da empresa (opcional)
 * @param {string} params.slogan - Slogan da empresa (opcional)
 * @param {boolean} params.mostrarMarcaDagua - Mostrar marca d'água
 * @param {Object} params.pdfConfig - Configurações personalizadas do PDF
 */
export async function exportFinancePDF({
  mes,
  registros,
  totalReceitas,
  totalDespesas,
  saldo,
  // 🏢 WHITE LABEL - DADOS DA EMPRESA
  empresa = "Imóveis Pro",
  usuario = "Corretor",
  logoBase64 = null,
  corPrimaria = "#2563eb",
  cnpj = "",
  telefone = "",
  slogan = "",
  mostrarMarcaDagua = true,
  // 📊 GRÁFICOS (removido html2canvas)
  // 📈 ESTATÍSTICAS ADICIONAIS
  vendas = {},
  alugueis = {},
  // ========================================================
  // 🔥 CONFIGURAÇÕES DE PDF
  // ========================================================
  pdfConfig = {}
}) {
  // ==========================================================================
  // 📋 EXTRAIR CONFIGURAÇÕES PDF (com valores padrão)
  // ==========================================================================
  const {
    tituloPadrao = "RELATÓRIO FINANCEIRO EXECUTIVO",
    mostrarCards = true,
    listaCompleta = false,
    tamanhoPapel = "A4",
    orientacao = "retrato"
  } = pdfConfig;

  // ==========================================================================
  // 🎨 CONFIGURAÇÃO DE CORES
  // ==========================================================================
  const whiteLabel = {
    nome: empresa,
    slogan: slogan,
    mostrarMarcaDagua: mostrarMarcaDagua,
    cidade: pdfConfig.cidade || '',
    uf: pdfConfig.uf || '',
    website: '',
    telefone: telefone,
    email: '',
    cor: corPrimaria
  };

  const primaryColor = hexToRgb(corPrimaria);
  const primary = [primaryColor.r, primaryColor.g, primaryColor.b];
  
  const dark = [30, 41, 59];
  const today = new Date();
  const dataAtual = today.toLocaleDateString("pt-BR");
  const horaAtual = today.toLocaleTimeString("pt-BR", { hour: '2-digit', minute: '2-digit' });

  // ==========================================================================
  // 📄 INICIALIZAÇÃO DO DOCUMENTO
  // ==========================================================================
  const doc = new jsPDF({
    orientation: orientacao === 'paisagem' ? 'landscape' : 'portrait',
    unit: 'mm',
    format: tamanhoPapel.toLowerCase()
  });

  // ===============================
  // 📌 MARCA D'ÁGUA OPCIONAL
  // ===============================
  if (mostrarMarcaDagua) {
    doc.setGState(new doc.GState({ opacity: 0.1 }));
    doc.setFontSize(50);
    doc.setTextColor(200, 200, 200);
    doc.text(empresa.toUpperCase(), 105, 140, {
      align: 'center',
      angle: 45
    });
    doc.setGState(new doc.GState({ opacity: 1 }));
  }

  // ===============================
  // 📌 HEADER CORPORATIVO
  // ===============================
  doc.setFillColor(...primary);
  doc.rect(0, 0, 210, 45, "F");

  doc.setTextColor(255, 255, 255);
  doc.setFontSize(22);
  doc.setFont('helvetica', 'bold');
  doc.text(empresa, 14, 22);

  if (slogan) {
    doc.setFontSize(10);
    doc.setFont('helvetica', 'italic');
    doc.setTextColor(240, 240, 240);
    doc.text(slogan, 14, 30);
  }

  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(255, 255, 255);
  doc.text(tituloPadrao.toUpperCase(), 14, 38);

  // Informações no canto direito do header
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(240, 240, 240);
  
  let rightY = 18;
  if (pdfConfig.cidade || pdfConfig.uf) {
    const cidadeUf = [pdfConfig.cidade, pdfConfig.uf].filter(Boolean).join(' - ');
    doc.text(cidadeUf, 140, rightY);
    rightY += 5;
  }
  
  doc.setFont('helvetica', 'bold');
  doc.text(`Exportado: ${dataAtual} ${horaAtual}`, 140, rightY);

  // Informações do período
  doc.setTextColor(...dark);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(`Período: ${mes.replace('-', '/')}`, 14, 60);
  doc.text(`Gerado em: ${dataAtual} ${horaAtual}`, 14, 67);

  let y = 80;

  // ===============================
  // 📌 RESUMO EXECUTIVO (CARDS)
  // ===============================
  if (mostrarCards) {
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...primary);
    doc.text("RESUMO EXECUTIVO", 14, y);
    doc.setDrawColor(...primary);
    doc.setLineWidth(0.5);
    doc.line(14, y + 2, 196, y + 2);
    y += 12;

    const resumoItems = [
      { label: "Receitas", value: `R$ ${totalReceitas.toFixed(2)}` },
      { label: "Despesas", value: `R$ ${totalDespesas.toFixed(2)}` },
      { label: "Saldo", value: `R$ ${saldo.toFixed(2)}` },
      { label: "Lançamentos", value: registros.length },
      { label: "Vendas", value: vendas?.mes || 0 },
      { label: "Aluguéis Ativos", value: alugueis?.ativos || 0 }
    ];

    // Layout em 3 colunas (igual ao relatório geral)
    let col = 0;
    resumoItems.forEach((item) => {
      const x = 14 + (col * 65);
      
      doc.setFillColor(248, 250, 252);
      doc.setDrawColor(226, 232, 240);
      doc.roundedRect(x, y - 3, 60, 18, 2, 2, 'FD');
      
      doc.setFontSize(7);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(100, 116, 139);
      doc.text(item.label, x + 3, y + 2);
      
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(15, 23, 42);
      const valorStr = item.value.toString();
      doc.text(valorStr, x + 3, y + 12);
      
      col++;
      if (col > 2) {
        col = 0;
        y += 22;
      }
    });

    y += 15;
  }

  // ===============================
  // 📌 RECEITAS POR CATEGORIA
  // ===============================
  const receitas = registros.filter(r => r.type === "receita");
  if (receitas.length > 0) {
    if (y > 250) {
      doc.addPage();
      y = 20;
    }

    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...primary);
    doc.text("RECEITAS POR CATEGORIA", 14, y);
    doc.setDrawColor(...primary);
    doc.line(14, y + 2, 196, y + 2);
    y += 10;

    // Agrupar por categoria
    const receitasPorCategoria = {};
    receitas.forEach(r => {
      const cat = r.category || "Outros";
      receitasPorCategoria[cat] = (receitasPorCategoria[cat] || 0) + (r.value || 0);
    });

    const categorias = Object.entries(receitasPorCategoria)
      .map(([cat, val]) => ({ categoria: cat, valor: val }))
      .sort((a, b) => b.valor - a.valor);

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');

    categorias.slice(0, 15).forEach((item) => {
      if (y > 280) {
        doc.addPage();
        y = 20;
      }
      
      doc.text(`- ${item.categoria}`, 14, y);
      doc.setFontSize(8);
      doc.setTextColor(34, 197, 94);
      doc.text(`R$ ${item.valor.toFixed(2)}`, 140, y, { align: 'right' });
      doc.setFontSize(10);
      doc.setTextColor(15, 23, 42);
      
      y += 7;
    });

    if (categorias.length > 15) {
      doc.text(`... e mais ${categorias.length - 15} categorias`, 14, y);
      y += 8;
    }
    y += 5;
  }

  // ===============================
  // 📌 DESPESAS POR CATEGORIA
  // ===============================
  const despesas = registros.filter(r => r.type === "despesa");
  if (despesas.length > 0) {
    if (y > 250) {
      doc.addPage();
      y = 20;
    }

    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...primary);
    doc.text("DESPESAS POR CATEGORIA", 14, y);
    doc.setDrawColor(...primary);
    doc.line(14, y + 2, 196, y + 2);
    y += 10;

    // Agrupar por categoria
    const despesasPorCategoria = {};
    despesas.forEach(d => {
      const cat = d.category || "Outros";
      despesasPorCategoria[cat] = (despesasPorCategoria[cat] || 0) + (d.value || 0);
    });

    const categorias = Object.entries(despesasPorCategoria)
      .map(([cat, val]) => ({ categoria: cat, valor: val }))
      .sort((a, b) => b.valor - a.valor);

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');

    categorias.slice(0, 15).forEach((item) => {
      if (y > 280) {
        doc.addPage();
        y = 20;
      }
      
      doc.text(`- ${item.categoria}`, 14, y);
      doc.setFontSize(8);
      doc.setTextColor(239, 68, 68);
      doc.text(`R$ ${item.valor.toFixed(2)}`, 140, y, { align: 'right' });
      doc.setFontSize(10);
      doc.setTextColor(15, 23, 42);
      
      y += 7;
    });

    if (categorias.length > 15) {
      doc.text(`... e mais ${categorias.length - 15} categorias`, 14, y);
      y += 8;
    }
    y += 5;
  }

  // ===============================
  // 📌 ÚLTIMOS LANÇAMENTOS
  // ===============================
  const lancamentos = listaCompleta ? registros : registros.slice(-20).reverse();
  
  if (lancamentos.length > 0) {
    if (y > 250) {
      doc.addPage();
      y = 20;
    }

    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...primary);
    doc.text("ÚLTIMOS LANÇAMENTOS", 14, y);
    doc.setDrawColor(...primary);
    doc.line(14, y + 2, 196, y + 2);
    y += 10;

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');

    lancamentos.slice(0, 20).forEach((item) => {
      if (y > 280) {
        doc.addPage();
        y = 20;
      }
      
      const data = item.date ? new Date(item.date).toLocaleDateString('pt-BR') : '--/--/----';
      const tipo = item.type === "receita" ? "RECEITA" : "DESPESA";
      const valor = item.value || 0;
      const categoria = item.category || "Geral";
      
      // Data
      doc.setFontSize(8);
      doc.setTextColor(100, 116, 139);
      doc.text(data, 14, y);
      
      // Tipo e Categoria
      doc.setFontSize(9);
      doc.setTextColor(item.type === "receita" ? 34 : 239, 
                       item.type === "receita" ? 197 : 68, 
                       item.type === "receita" ? 94 : 68);
      doc.text(tipo, 40, y);
      
      doc.setFontSize(8);
      doc.setTextColor(100, 116, 139);
      doc.text(categoria, 70, y);
      
      // Valor
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(item.type === "receita" ? 34 : 239, 
                       item.type === "receita" ? 197 : 68, 
                       item.type === "receita" ? 94 : 68);
      doc.text(`R$ ${valor.toFixed(2)}`, 170, y, { align: 'right' });
      
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(15, 23, 42);
      
      y += 7;
    });

    if (!listaCompleta && registros.length > 20) {
      doc.text(`... e mais ${registros.length - 20} lançamentos`, 14, y);
      y += 8;
    }
  }

  // ===============================
  // 📌 RODAPÉ PROFISSIONAL
  // ===============================
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    
    doc.setDrawColor(...primary);
    doc.setLineWidth(0.3);
    doc.line(14, 285, 196, 285);
    
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100, 116, 139);
    
    let contactText = empresa;
    if (telefone) contactText += ` - ${telefone}`;
    if (cnpj) contactText += ` - CNPJ: ${cnpj}`;
    
    doc.text(contactText, 14, 290);
    
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...primary);
    doc.text(`Página ${i} de ${totalPages}`, 150, 290);
    
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(6);
    doc.setTextColor(180, 180, 180);
    doc.text('Relatório financeiro gerado pelo sistema', 14, 295);
  }

  // ===============================
  // 📌 DOWNLOAD
  // ===============================
  const nomeArquivo = `relatorio-financeiro-${mes.replace('/', '-')}.pdf`;
  doc.save(nomeArquivo);

  return doc;
}

export default exportFinancePDF;